import React, { useContext, useState } from "react";
import { Box, Divider, Flex, Link, Text } from "flicket-ui";
import { FormikHelpers, FormikValues } from "formik";
import { useGoogleLogin } from "react-google-login";

import { Footer, Menu, Seo } from "~components";
import { googleClientId } from "~config";
import { BaseContext } from "~context";
import { useIsMobile, useAccount, useFacebookLogin } from "~hooks";
import {
  ContentContainer,
  AuthContainer,
  MobileLogo,
  SocialButtons,
  SignupForm,
} from "~components/auth";
import { Layout } from "~components/common/Layout";
import { SocialAuthType } from "~graphql/sdk";
import { showToast } from "~lib/helpers";
import { useReferralCode } from "~hooks/useReferralCode";

const Signup = () => {
  const { organization } = useContext(BaseContext);
  const isMobile = useIsMobile();
  const { register, handleSocialSuccess } = useAccount();
  const [isAwaitingVerification, setAwaitingVerification] = useState(false);
  const { referralCode } = useReferralCode();

  const { isFacebookLoading, handleFacebookLogin } = useFacebookLogin({
    onSuccess: handleSocialSuccess,
  });

  const { signIn } = useGoogleLogin({
    onSuccess: (response: any) => {
      handleSocialSuccess(
        response?.getAuthResponse(true)?.access_token,
        SocialAuthType.Google
      );
    },
    clientId: googleClientId,
    onFailure: (response) => {
      if (
        !["popup_closed_by_user", "idpiframe_initialization_failed"].includes(
          response?.error
        )
      ) {
        showToast("Error logging in with Google", "error");
      }
    },
  });

  const handleSubmit = async (
    {
      firstName,
      lastName,
      email,
      password,
      enableTwoFactorAuthentication,
    }: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    const user = await register({
      firstName,
      lastName,
      email,
      password,
      enableTwoFactorAuthentication,
      userAcquisition: { referralCodeId: referralCode },
    });
    if (!user) {
      actions.setSubmitting(false);
    } else if (!user?.hasVerifiedEmail) {
      setAwaitingVerification(true);
    }
  };

  return (
    <>
      <Seo title="Signup" description="Signup page for" />
      <Menu />
      <Layout layoutType="auth">
        <ContentContainer>
          <MobileLogo logo={organization?.branding?.logo} />
          <Flex
            bg="N100"
            justifyContent="flex-start"
            flex={1}
            alignItems="center"
            flexDir="column"
          >
            <AuthContainer>
              <Box w="100%">
                {isAwaitingVerification ? (
                  <>
                    <Text
                      fontSize={6}
                      mb={3}
                      fontWeight="heavy"
                      color="N800"
                      textAlign="center"
                    >
                      Awaiting verification
                    </Text>

                    <Text variant="paragraph" mb={2}>
                      A login link has been sent to your email address to verify
                      your account.
                      <br />
                      If you can’t find the email check your spam folder, or
                      request a new one from the login page.
                    </Text>
                  </>
                ) : (
                  <>
                    <Text
                      fontSize={6}
                      fontWeight="heavy"
                      color="N800"
                      textAlign="center"
                    >
                      Sign up
                    </Text>
                    <SocialButtons
                      handleGoogleClick={signIn}
                      isFacebookLoading={isFacebookLoading}
                      handleFacebookClick={handleFacebookLogin}
                    />
                    <Box my={2}>
                      <Divider color="N200">
                        <Text
                          fontSize={2}
                          lineHeight={"18px" as any}
                          color="N800"
                        >
                          Or
                        </Text>
                      </Divider>
                    </Box>
                    <SignupForm handleSubmit={handleSubmit} />
                  </>
                )}
              </Box>
            </AuthContainer>
            <Flex
              justifyContent="center"
              alignItems="center"
              mt={2}
              mb={{ _: 3, md: "28px" as any }}
            >
              <Text
                fontSize={1}
                lineHeight="high"
                fontWeight="demiBold"
                mr="1/2"
              >
                Already have an account?
              </Text>
              <Link
                to="/login"
                color="P300"
                textDecoration="underline"
                fontWeight="heavy"
                fontSize={1}
              >
                Log in
              </Link>
            </Flex>
          </Flex>
          {!isMobile && <Footer />}
        </ContentContainer>
      </Layout>
    </>
  );
};

export default Signup;
